import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import Icon from 'Components/Atoms/Icon/Icon';
import InvoicesInboxEmailAttachment from './InvoicesInboxEmailAttachment';
import { formatLongDate } from 'Utils/formatters';
import useLocale from 'Hooks/useLocale';
import { useTranslation } from 'react-i18next';
import { deleteInvoiceEmail, processInvoiceEmail } from 'Services/invoices';
import { useQueryClient } from '@tanstack/react-query';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import useInvoicesEmailRender, { CONTENT_TYPE } from 'Hooks/useInvoicesEmailRender';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { trackError } from 'Utils/errorMonitoring';
import Spinner from 'Components/Atoms/Spinner';
import { cn } from 'Utils/cn';
import { sanitizeHtml } from 'Utils/sanitizeHtml';

function getFileType(fileName) {
    const extension = fileName.toLowerCase().split('.').pop();

    switch (extension) {
        case 'pdf':
            return 'application/pdf';
        case 'doc':
        case 'docx':
            return 'application/msword';
        case 'jpg':
        case 'jpeg':
        case 'png':
            return 'image/jpeg';
        default:
            return 'application/octet-stream';
    }
}

export default function InvoicesInboxEmailDrawer(props) {
    const { onClose, email } = props;

    const [processing, setProcessing] = useState(false);
    const [retrying, setRetrying] = useState(false);

    const { body, contentType, isLoading } = useInvoicesEmailRender(email.id);

    const locale = useLocale();
    const { t } = useTranslation('invoices');
    const queryClient = useQueryClient();
    const { showUnknownError } = useErrors();
    const { enqueueSnackbar } = useSnackbar();

    const getErrorMessage = (error) => {
        switch (error) {
            case 'requires_paid_subscription':
                return t('email-errors.requires-paid-subscription');
            case 'image_too_small':
                return t('email-errors.image-too-small');
            case 'invalid_file_extension':
                return t('email-errors.invalid-file-extension');
            default:
                return t('email-errors.unknown-error');
        }
    };

    const handleDelete = async () => {
        setProcessing(true);
        try {
            await deleteInvoiceEmail(email.id);
            await queryClient.invalidateQueries({ queryKey: ['invoices', 'emails'] });
            onClose();
        } catch (error) {
            showUnknownError(error);
        } finally {
            setProcessing(false);
        }
    };

    const handleRetry = async () => {
        setRetrying(true);
        try {
            await processInvoiceEmail(email.id);
            enqueueSnackbar(t('reprocessing-email'), { key: email.id, variant: 'processing' });
        } catch (error) {
            trackError(error);
        }
    };

    return (
        <>
            <div className="flex gap-x-2 flex-wrap py-4 mb-4 border-b border-gray-200 text-xs text-gray-500 justify-between">
                <div>{email.sender}</div>
                <div>{formatLongDate(email.date, { locale })}</div>
            </div>

            <div className="flex gap-2 mb-6">
                <Button onClick={handleRetry} size="extraSmall" disabled={retrying}>
                    <Icon name="refresh" size={16} color="currentColor" />
                    {t('retry')}
                </Button>

                <Button isBusy={processing} onClick={handleDelete} size="extraSmall">
                    <Icon name="trash" size={16} color="currentColor" />
                    {t('delete')}
                </Button>
            </div>

            <div className="mb-6">
                <div className="uppercase font-semibold text-2xs tracking-widest">{t('files')}</div>
                <div className="py-1">
                    <ul className="grid gap-4">
                        {email.files.map((file) => (
                            <li key={file.id}>
                                <InvoicesInboxEmailAttachment
                                    emailId={email.id}
                                    file={{
                                        id: file.id,
                                        name: file.name,
                                        size: file.size,
                                        type: getFileType(file.name),
                                        creation_date: file.creation_date,
                                    }}
                                    error={
                                        file.status === 'ERROR'
                                            ? getErrorMessage(file.status_message?.description)
                                            : null
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="mb-6">
                <div className="uppercase font-semibold text-2xs tracking-widest">{t('email')}</div>
                <div className="py-1">
                    {isLoading && (
                        <div className="email-preview">
                            <div className="py-5 flex justify-center">
                                <Spinner />
                            </div>
                        </div>
                    )}

                    {contentType === CONTENT_TYPE.PLAIN && !isLoading && (
                        <div className={cn('email-preview', 'white-space-collapse-pre')}>{body}</div>
                    )}

                    {contentType !== CONTENT_TYPE.PLAIN && !isLoading && (
                        <div className="email-preview " dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }} />
                    )}
                </div>
            </div>
        </>
    );
}

InvoicesInboxEmailDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    email: PropTypes.object.isRequired,
};
